import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../App';
import ProgramCard from '../../components/portal/offers/ProgramCard';
import MembershipSection from '../../components/portal/offers/MembershipSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });
const muted = { color: 'var(--p-muted)' };

export default function ClientPrograms() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [offers, setOffers] = useState(null);
  const [membership, setMembership] = useState(null);
  const [busy, setBusy] = useState('');
  const navigate = useNavigate();

  const loadMembership = useCallback(() => ax.get(`${API_URL}/api/portal/membership`).then((r) => setMembership(r.data.membership)).catch(() => {}), []);

  useEffect(() => {
    ax.get(`${API_URL}/api/offers`).then((r) => setOffers(r.data)).catch(() => setOffers({ programs: [], memberships: [], tool_choices: [] }));
    loadMembership();
  }, [loadMembership]);

  useEffect(() => {
    if (offers && window.location.hash === '#memberships') setTimeout(() => document.getElementById('memberships')?.scrollIntoView({ behavior: 'smooth' }), 150);
  }, [offers]);

  useEffect(() => {
    const sid = params.get('membership_session');
    if (!sid) return;
    ax.get(`${API_URL}/api/memberships/confirm`, { params: { session_id: sid } })
      .then((r) => { if (r.data.active) { toast.success('Membership active! Your tools are unlocked.'); loadMembership(); } })
      .catch(() => toast.error('Could not confirm membership yet. Refresh in a moment.'))
      .finally(() => setParams({}, { replace: true }));
  }, [params, setParams, loadMembership]);

  const go = async (key, fn) => {
    setBusy(key);
    try { const r = await fn(); window.location.href = r.data.url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout'); setBusy(''); }
  };
  const enroll = (p) => p.package_id === 'pkg_dca_strategy' ? navigate('/portal/book') : go(p.package_id, () => ax.post(`${API_URL}/api/coaching-packages/checkout`, {
    brand_slug: 'dca', package_id: p.package_id, name: user?.name || user?.email, email: user?.email, origin_url: window.location.origin,
  }));
  const joinMembership = (t, tools) => go(t.tier, () => ax.post(`${API_URL}/api/memberships/checkout`, { tier: t.tier, tools, origin_url: window.location.origin }));
  const cancel = async () => {
    if (!window.confirm('Cancel your membership at the end of this billing period?')) return;
    try { await ax.post(`${API_URL}/api/portal/membership/cancel`); toast.success('Membership will end at the close of this period'); loadMembership(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
  };

  return (
    <PortalLayout>
      <div className="mb-8" data-testid="portal-programs">
        <span className="text-xs tracking-[0.2em] uppercase text-[#F97316] font-semibold">Coaching programs</span>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose your level of support</h1>
        <p className="mt-3 max-w-2xl" style={muted}>Every program includes real coaching with David, weekly check-ins and the tools to build your business. Compare side by side, then enroll or talk to David first.</p>
      </div>

      <div className="rounded-2xl p-6 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border" style={{ background: 'linear-gradient(100deg, rgba(249,115,22,0.10), var(--p-surface))', borderColor: 'rgba(249,115,22,0.35)' }} data-testid="portal-programs-free">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#F97316] uppercase tracking-wider mb-1"><Sparkles size={12} /> Not sure yet? Start free</div>
          <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>FREE 20-Minute Business Jumpstart Session</h3>
          <p className="text-sm mt-1" style={muted}>A no-pitch strategy call. David will tell you honestly which option fits, or if you need one at all.</p>
        </div>
        <Link to="/portal/book" className="shrink-0 inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-3 rounded-xl font-medium transition-colors" data-testid="portal-programs-book-free">
          Book free session <ArrowRight size={16} />
        </Link>
      </div>

      {!offers ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 pt-3" data-testid="portal-programs-grid">
            {offers.programs.map((p) => <ProgramCard key={p.package_id} p={p} busy={busy === p.package_id} onEnroll={enroll} />)}
          </div>
          <p className="text-xs mt-4" style={{ color: 'var(--p-faint)' }}>Program tools unlock automatically right after payment. Secure checkout by Stripe.</p>
          <MembershipSection tiers={offers.memberships} choices={offers.tool_choices} membership={membership} busy={busy} onCheckout={joinMembership} onCancel={cancel} />
        </>
      )}
    </PortalLayout>
  );
}
